import { format } from 'date-fns';
import {
  Image,
  StyleProp,
  StyleSheet,
  TouchableOpacity,
  View,
  ViewStyle,
} from 'react-native';
import { mvs } from 'react-native-size-matters';

import { useAppSelector } from '../../app/hooks';
import { IS_WEB } from '../../constants';
import { selectDivRadius } from '../../features/appconfig/store/selectors';
import { useAdjustedFontSize } from '../../hooks/useAdjustedFontSize';
import { SongHistoryEntry } from '../../models';
import CustomText from '../CustomText';

interface SongHistoryCardProps {
  song: SongHistoryEntry;
  onPress?: () => void;
  style?: StyleProp<ViewStyle>;
}

export const SongHistoryCard = ({ song, onPress, style }: SongHistoryCardProps) => {
  const formattedTime = song.date ? format(new Date(song.date), 'HH:mm') : '';
  const divRadius = useAppSelector(selectDivRadius);
  const titleFontSize = useAdjustedFontSize(mvs(16));
  const artistFontSize = useAdjustedFontSize(mvs(16));
  const timeFontSize = useAdjustedFontSize(mvs(16));

  const content = (
    <>
      {/* album cover */}
      <View style={styles.coverContainer}>
        {song.cover ? (
          <Image
            source={{ uri: song.cover }}
            style={[styles.cover, { borderRadius: divRadius * 0.7 }]}
          />
        ) : (
          <View style={[styles.coverPlaceholder, { borderRadius: divRadius * 0.7 }]} />
        )}
      </View>

      {/* song info */}
      <View style={styles.infoContainer}>
        <View style={styles.textContainer}>
          <CustomText
            style={[styles.title, { fontSize: titleFontSize }]}
            numberOfLines={1}
            weight="Bold"
          >
            {song.title}
          </CustomText>
          <CustomText
            style={[styles.artist, { fontSize: artistFontSize }]}
            numberOfLines={1}
          >
            {song.artist}
          </CustomText>
        </View>
        <CustomText style={[styles.time, { fontSize: timeFontSize }]} weight="SemiBold">
          {formattedTime}
        </CustomText>
      </View>
    </>
  );

  // on web, render as plain view without touch interaction
  if (IS_WEB) {
    return (
      <View style={[styles.container, { borderRadius: divRadius }, style]}>
        {content}
      </View>
    );
  }

  // on mobile, render as touchable component
  return (
    <TouchableOpacity
      style={[styles.container, { borderRadius: divRadius }, style]}
      onPress={onPress}
      activeOpacity={0.7}
      disabled={!onPress}
    >
      {content}
    </TouchableOpacity>
  );
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: '#FFFFFF',
    paddingLeft: mvs(8),
    paddingRight: mvs(16),
    paddingVertical: mvs(8),
    flexDirection: 'row',
    alignItems: 'center',
    gap: mvs(12),
  },
  coverContainer: {
    width: mvs(50),
    height: mvs(50),
  },
  cover: {
    width: '100%',
    height: '100%',
  },
  coverPlaceholder: {
    width: '100%',
    height: '100%',
    backgroundColor: '#E0E0E0',
  },
  infoContainer: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  textContainer: {
    flex: 1,
    color: '#252525',
  },
  title: {
    lineHeight: mvs(20),
  },
  artist: {
    opacity: 0.7,
    lineHeight: mvs(20),
  },
  time: {
    opacity: 0.7,
    marginLeft: mvs(8),
    textAlign: 'right',
  },
});
